import type { Row, Table } from "@tanstack/react-table";
import { Loader2, PencilIcon, Search, UploadIcon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SmbAttachmentStatus, type Transaction } from "@/apis/gql/types";
import { DocumentUploadDropzone } from "@/components/shared/dropzones/document-upload-dropzone";
import { calculatePaymentDiscrepancy } from "@/lib/ledger/journal-entry-utils";
import { useTenant } from "@/providers/session";
import { useAddInvoiceToTransaction } from "@/queries/transactions/add-invoice";

import { Button } from "@/components/ui/button";
import { DiscrepancyReason } from "@/lib/constants/discrepancy-reason";
import { InvoiceSuggestionCell } from "./invoice-suggestion-cell";

type Props = {
  row: Row<Transaction>;
  transaction: Transaction;
  table: Table<Transaction>;
  onConfirmBooking: () => Promise<void>;
  onDirectBooking: () => Promise<void>;
  onUnmatch: (invoiceIds: string[]) => Promise<void>;
};

export const InvoiceCell = ({
  row,
  transaction,
  table,
  onConfirmBooking,
  onDirectBooking,
  onUnmatch,
}: Props) => {
  const { tenantId } = useTenant();
  const { t } = useTranslation();
  const [addInvoiceToTransaction] = useAddInvoiceToTransaction();

  const handleSuccess = (invoiceIds: string[]) => {
    addInvoiceToTransaction({
      bankTxnIds: [transaction.bankTxnId],
      invoiceIds,
    });
  };

  const invoices: Array<
    (typeof transaction.invoices)[number] & {
      discrepancyRule: DiscrepancyReason;
    }
  > = useMemo(() => {
    return transaction.invoices.reduce<
      Array<
        (typeof transaction.invoices)[number] & {
          discrepancyRule: DiscrepancyReason;
        }
      >
    >((acc, invoice) => {
      const { isExactMatch, isOverpayment, isUnderpayment, discrepancy } =
        calculatePaymentDiscrepancy([
          {
            invoices: [invoice],
            side: transaction.side,
            amount: transaction.amount,
          },
        ]);

      const getDiscrepancyRule = () => {
        if (isOverpayment) {
          if (invoice.currency !== transaction.currency) {
            return DiscrepancyReason.FXLOSS;
          }
        }
        if (isUnderpayment) {
          if (invoice.currency !== transaction.currency) {
            return DiscrepancyReason.FXGAIN;
          }

          if (invoice.currency === transaction.currency && discrepancy < 0) {
            return DiscrepancyReason.DISCOUNT;
          }
        }

        if (isExactMatch) {
          return DiscrepancyReason.EXACT_MATCH;
        }
      };

      const discrepancyRule = getDiscrepancyRule();

      if (
        discrepancyRule &&
        transaction.smbAttachmentStatus ===
          SmbAttachmentStatus.ATTACHMENT_SUGGESTED
      ) {
        acc.push({
          ...invoice,
          discrepancyRule,
        });
      }

      return acc;
    }, []);
  }, [transaction]);

  if (invoices.length > 0) {
    return (
      <InvoiceSuggestionCell
        bankTransaction={transaction}
        invoices={invoices}
        onConfirmBooking={onConfirmBooking}
        onUnmatch={onUnmatch}
      />
    );
  }

  return (
    <DocumentUploadDropzone
      tenantId={tenantId}
      clickUploadEnabled
      onSuccess={handleSuccess}
    >
      {({ isUploading }) => (
        <div className="flex items-center justify-center w-full px-2 my-2">
          <div className="flex flex-row items-center justify-center h-auto border-2 bg-gray-50 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-100 p-4 gap-2 w-full flex-wrap">
            <div className="flex flex-row items-center justify-center gap-2 text-gray-500">
              {isUploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <UploadIcon className="w-5 h-5" />
              )}
              <p className="text-sm">
                <span className="font-semibold">
                  {isUploading ? t("uploading") : t("upload_here")}
                </span>
              </p>
            </div>
            <span className="text-xs text-muted-foreground">{t("or")}</span>
            <Button
              variant="outline"
              className="gap-2 h-8 px-3"
              onClick={(event) => handleRowClick(event, row)}
            >
              <Search className="w-4 h-4" />
              {t("transactions.search")}
            </Button>
            <span className="text-xs text-muted-foreground">{t("or")}</span>
            <Button
              variant="outline"
              className="gap-2 h-8 px-3"
              onClick={handleDirectBooking}
            >
              <PencilIcon className="w-4 h-4" />
              {t("transactions.actions.direct_booking")}
            </Button>
          </div>
        </div>
      )}
    </DocumentUploadDropzone>
  );

  function handleRowClick(event: React.MouseEvent, row: Row<Transaction>) {
    event.preventDefault();
    event.stopPropagation();
    table.setRowSelection((prev) => ({
      [row.id]: !prev[row.id],
    }));
  }

  function handleDirectBooking(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    onDirectBooking();
  }
};
